<template>
  <div class="space-y-6" :aria-busy="loading">
    <!-- Bloque nuevo: todavía no hay quiz asociado -->
    <div v-if="!modelValue.quiz_id" class="py-4 text-center">
      <p class="text-sm text-gray-500">{{ $t('projects.quiz_empty') }}</p>
      <button type="button" v-on:click="createQuiz" :disabled="creating"
              class="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-blue-500 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
        <font-awesome-icon v-if="creating" class="mr-2 h-4 w-4 animate-spin" icon="spinner"/>
        {{ $t('projects.quiz_create') }}
      </button>
    </div>

    <div v-else-if="loading" class="py-4 text-center text-sm text-gray-500">
      <font-awesome-icon class="mr-2 h-5 w-5 animate-spin" icon="spinner"/>
      {{ $t('projects.quiz_loading') }}
    </div>

    <div v-else-if="!quiz" class="py-4 text-center">
      <p class="text-sm text-gray-500">{{ $t('projects.quiz_load_error') }}</p>
      <button type="button" v-on:click="loadQuiz"
              class="mt-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
        {{ $t('projects.retry') }}
      </button>
    </div>

    <template v-else>
      <quiz-settings-form :quiz="quiz" :id-prefix="blockId + '-settings'" :saving="savingSettings" @save="saveSettings"></quiz-settings-form>

      <section>
        <h4 class="text-sm font-semibold text-gray-700">{{ $t('projects.quiz_questions_title', {count: quiz.questions.length}) }}</h4>
        <p v-if="!quiz.questions.length" class="mt-2 text-sm text-gray-500">{{ $t('projects.quiz_no_questions') }}</p>

        <vue-draggable v-model="quiz.questions" handle=".question-drag-handle" :animation="200"
                       :disabled="reordering || editingQuestion !== null" @end="onQuestionsDragEnd">
          <div v-for="(question, index) in quiz.questions" :key="question.uuid" class="mt-3">
            <quiz-question-form v-if="editingQuestion === question.uuid" :question="question" :id-prefix="blockId + '-q-' + question.uuid"
                                :saving="savingQuestion" @save="saveQuestion" @cancel="editingQuestion = null"></quiz-question-form>

            <article v-else class="rounded-md border border-gray-200 bg-white p-3">
              <header class="flex items-start gap-2">
                <span class="question-drag-handle mt-0.5 cursor-grab active:cursor-grabbing text-stone-400 hover:text-stone-600"
                      :title="$t('projects.quiz_drag_question')" aria-hidden="true">
                  <font-awesome-icon icon="grip-lines" class="h-4 w-4" aria-hidden="true"/>
                </span>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900">{{ index + 1 }}. {{ question.statement }}</p>
                  <p class="text-xs text-gray-500">{{ $t('projects.quiz_type_' + question.type) }}</p>
                </div>
                <button type="button" v-on:click="moveQuestion(index, -1)" :disabled="reordering || index === 0"
                        :title="$t('projects.quiz_move_question_up')" :aria-label="$t('projects.quiz_move_question_up')"
                        class="p-1 rounded text-gray-500 hover:text-gray-700 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue">
                  <font-awesome-icon icon="chevron-up" class="h-4 w-4" aria-hidden="true"/>
                </button>
                <button type="button" v-on:click="moveQuestion(index, 1)" :disabled="reordering || index === quiz.questions.length - 1"
                        :title="$t('projects.quiz_move_question_down')" :aria-label="$t('projects.quiz_move_question_down')"
                        class="p-1 rounded text-gray-500 hover:text-gray-700 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue">
                  <font-awesome-icon icon="chevron-down" class="h-4 w-4" aria-hidden="true"/>
                </button>
                <button type="button" v-on:click="editingQuestion = question.uuid" :disabled="editingQuestion !== null"
                        :title="$t('projects.quiz_edit_question')" :aria-label="$t('projects.quiz_edit_question')"
                        class="p-1 rounded text-blue-500 hover:text-blue-300 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue">
                  <font-awesome-icon icon="pen" class="h-4 w-4" aria-hidden="true"/>
                </button>
                <button type="button" v-on:click="questionToDelete = question" :disabled="editingQuestion !== null"
                        :title="$t('projects.quiz_delete_question')" :aria-label="$t('projects.quiz_delete_question')"
                        class="p-1 rounded text-red-600 hover:text-red-400 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue">
                  <font-awesome-icon icon="trash" class="h-4 w-4" aria-hidden="true"/>
                </button>
              </header>
              <ul class="mt-2 ml-7 space-y-1">
                <li v-for="option in question.options" :key="option.uuid ?? option.text"
                    class="flex items-center text-sm" :class="option.is_correct ? 'text-green-700 font-medium' : 'text-gray-600'">
                  <font-awesome-icon v-if="option.is_correct" icon="check" class="mr-1.5 h-4 w-4 flex-shrink-0" aria-hidden="true"/>
                  <span v-else class="mr-1.5 h-4 w-4 flex-shrink-0" aria-hidden="true"></span>
                  <span>{{ option.text }}</span>
                  <span v-if="option.is_correct" class="sr-only">({{ $t('projects.quiz_correct') }})</span>
                </li>
              </ul>
            </article>
          </div>
        </vue-draggable>

        <div class="mt-3">
          <quiz-question-form v-if="editingQuestion === 'new'" :id-prefix="blockId + '-q-new'" :saving="savingQuestion"
                              @save="saveQuestion" @cancel="editingQuestion = null"></quiz-question-form>
          <button v-else type="button" v-on:click="editingQuestion = 'new'" :disabled="editingQuestion !== null"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
            + {{ $t('projects.quiz_add_question') }}
          </button>
        </div>
      </section>
    </template>

    <modal-component v-if="questionToDelete" :on-process="deleteQuestion" :on-cancel="cancelDeleteQuestion"
                     :loading="deletingQuestion" on-process-title="projects.quiz_delete_question">
      <template v-slot:header>{{ $t('projects.quiz_delete_question_title') }}</template>
      {{ $t('projects.quiz_delete_question_text') }}
    </modal-component>
  </div>
</template>

<script>
import { VueDraggable } from 'vue-draggable-plus';
import ModalComponent from "@/components/modals/ModalComponent";
import NotificationService from "@/services/notification/NotificationService";
import QuizService from "@/services/project/QuizService";
import QuizSettingsForm from "@/views/projects/ProjectEdit/Blocks/Quiz/QuizSettingsForm";
import QuizQuestionForm from "@/views/projects/ProjectEdit/Blocks/Quiz/QuizQuestionForm";

const DEFAULT_PASS_PERCENTAGE = 50
const byPosition = (a, b) => (a.position ?? 0) - (b.position ?? 0)
const unwrap = (response) => response.data?.data ?? response.data

/*
 * Bloque de quiz (key `quiz`, data `{ quiz_id }` con el uuid del quiz, LMS-072).
 * El quiz es un recurso propio (LMS-091): sus ajustes y preguntas se guardan al
 * momento con sus botones; el bloque solo guarda la referencia al quiz.
 */
export default {
  name: "QuizBlock",
  components: {
    VueDraggable,
    ModalComponent,
    QuizSettingsForm,
    QuizQuestionForm
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    blockId: {
      type: String,
      required: true
    },
    lessonUuid: {
      type: String,
      required: true
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      quiz: null,
      loading: false,
      creating: false,
      savingSettings: false,
      savingQuestion: false,
      reordering: false,
      deletingQuestion: false,
      // uuid de la pregunta en edición, 'new' al crear una o null.
      editingQuestion: null,
      questionToDelete: null,
    }
  },
  watch: {
    'modelValue.quiz_id'(quizId, previousQuizId) {
      if (quizId && quizId !== this.quiz?.uuid && quizId !== previousQuizId) this.loadQuiz()
    }
  },
  created() {
    if (this.modelValue.quiz_id) this.loadQuiz()
  },
  methods: {
    setQuiz(quiz) {
      this.quiz = {...quiz, questions: (quiz.questions ?? []).slice().sort(byPosition)}
    },
    loadQuiz() {
      this.loading = true
      QuizService.show(this.modelValue.quiz_id)
          .then((response) => this.setQuiz(unwrap(response)))
          .catch((error) => {
            this.quiz = null
            NotificationService.showApiError(error, this.$t('projects.quiz_load_error'))
          })
          .finally(() => {
            this.loading = false
          })
    },
    createQuiz() {
      this.creating = true
      QuizService.store(this.lessonUuid, {
        title: this.$t('projects.quiz_default_title'),
        pass_percentage: DEFAULT_PASS_PERCENTAGE,
        max_attempts: null,
        required_to_progress: false,
        shuffle: false,
      })
          .then((response) => {
            this.setQuiz(unwrap(response))
            this.$emit('update:modelValue', {...this.modelValue, quiz_id: this.quiz.uuid})
            NotificationService.showNotification(this.$t('projects.quiz_created'), "success")
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.quiz_save_error'))
          })
          .finally(() => {
            this.creating = false
          })
    },
    saveSettings(settings) {
      this.savingSettings = true
      QuizService.update({...settings, uuid: this.quiz.uuid})
          .then(() => {
            this.quiz = {...this.quiz, ...settings}
            NotificationService.showNotification(this.$t('projects.quiz_settings_saved'), "success")
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.quiz_save_error'))
          })
          .finally(() => {
            this.savingSettings = false
          })
    },
    saveQuestion(question) {
      const request = question.uuid
          ? QuizService.updateQuestion(this.quiz.uuid, question)
          : QuizService.storeQuestion(this.quiz.uuid, question)

      this.savingQuestion = true
      request
          .then((response) => {
            const saved = {...question, ...unwrap(response)}
            const index = this.quiz.questions.findIndex((item) => item.uuid === saved.uuid)
            if (index === -1) {
              this.quiz.questions.push(saved)
            } else {
              this.quiz.questions.splice(index, 1, saved)
            }
            this.editingQuestion = null
            NotificationService.showNotification(this.$t('projects.quiz_question_saved'), "success")
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.quiz_save_error'))
          })
          .finally(() => {
            this.savingQuestion = false
          })
    },
    deleteQuestion() {
      const question = this.questionToDelete

      this.deletingQuestion = true
      QuizService.destroyQuestion(this.quiz.uuid, question.uuid)
          .then(() => {
            this.quiz.questions = this.quiz.questions.filter((item) => item.uuid !== question.uuid)
            NotificationService.showNotification(this.$t('projects.quiz_question_deleted'), "success")
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.quiz_save_error'))
          })
          .finally(() => {
            this.deletingQuestion = false
            this.questionToDelete = null
          })
    },
    cancelDeleteQuestion() {
      this.questionToDelete = null
    },
    moveQuestion(index, offset) {
      const target = index + offset
      if (target < 0 || target >= this.quiz.questions.length) return

      const previousOrder = this.quiz.questions.slice()
      const [question] = this.quiz.questions.splice(index, 1)
      this.quiz.questions.splice(target, 0, question)
      this.persistOrder(previousOrder)
    },
    onQuestionsDragEnd(event) {
      if (event.oldIndex === event.newIndex) return

      // vue-draggable ya movió la pregunta: se reconstruye el orden anterior para el rollback.
      const previousOrder = this.quiz.questions.slice()
      const [question] = previousOrder.splice(event.newIndex, 1)
      previousOrder.splice(event.oldIndex, 0, question)
      this.persistOrder(previousOrder)
    },
    // Actualización optimista: si la API falla se restaura el orden anterior.
    persistOrder(previousOrder) {
      this.reordering = true
      QuizService.reorderQuestions(this.quiz.uuid, this.quiz.questions)
          .catch((error) => {
            this.quiz.questions = previousOrder
            NotificationService.showApiError(error, this.$t('projects.quiz_order_error'))
          })
          .finally(() => {
            this.reordering = false
          })
    },
  }
}
</script>
